import React, { useMemo, useState } from "react";
import { CCCombobox, CCTagList } from "@pymblecitizencoders/cc-ui";
import { FacetOption } from "../../utils/eventFacets";
import { normaliseTag } from "../../utils/eventTags";

/**
 * The rail's tag filter: search the tags, pick the ones to narrow the calendar
 * by, and see which are on.
 *
 * ## FILTERING ONLY
 *
 * This used to share the facet with bulk assignment — a find-or-create field
 * that armed a tag, a second "Search tags" box that filtered the rows, and a
 * tick box plus an arm icon on every row. Two search boxes a few pixels apart
 * doing different things, and two verbs per row, for what most people use as a
 * filter. Assignment now lives in the "Tag events" bar over the calendar
 * (see EventList), so this control does one job and never writes.
 *
 * It therefore offers no "Create ..." row: a tag nobody has used yet carries no
 * events, so filtering by one could only ever empty the calendar.
 *
 * ## Why not `CCMultiSelect`
 *
 * It builds its chips from `options`, so a ticked tag that has left the
 * catalogue would lose its chip altogether while still filtering the calendar.
 *
 * ## No counts
 *
 * The options are the tag names alone. Counts were tried and removed: with a
 * tag ticked the others cannot be counted (the API has already narrowed the
 * response), so the numbers came and went as the filter changed. Chips come from
 * `selected` instead, the same bargain EventTagField strikes in the editor.
 *
 * ## Async mode is deliberate
 *
 * Passing `onQueryChange` turns CCCombobox's own filtering off, so the list is
 * exactly the options handed over: the tags not already chosen, in the
 * facet's alphabetical order, narrowed by what was typed.
 */

interface EventTagFilterProps {
	/** The tag facet from `buildFacets`. */
	options: FacetOption[];
	/** Tag ids currently filtering the calendar. */
	selected: string[];
	onChange: (tags: string[]) => void;
}

/** Suggestions shown at once. The rest are a more specific query away. */
const MAX_SUGGESTIONS = 50;

/**
 * Open the list when the box is focused or clicked, not only once typing
 * starts.
 *
 * CCCombobox opens on typing, ArrowDown or its chevron, and has no prop for
 * this or any way to reach its input. So a focus or click on the INPUT that
 * finds the list closed (`aria-expanded`, which the kit keeps in step) sends it
 * the ArrowDown it already treats as "open with everything". Going through the
 * kit's own key handler rather than around it means its state, highlight and
 * `onQueryChange` all stay consistent.
 *
 * Click as well as focus because the box KEEPS focus after a tag is picked or
 * Escape is pressed: the next click does not refocus it, so focus alone would
 * leave the list shut until the person tabbed away and back.
 *
 * TODO: replace with an `openOnFocus` prop once cc-ui has one.
 */
const openIfClosed = (e: React.SyntheticEvent) => {
	const input = e.target as HTMLElement;
	if (!input.classList.contains("cc-combo__input")) return;
	if (input.getAttribute("aria-expanded") === "true") return;
	input.dispatchEvent(
		new KeyboardEvent("keydown", { key: "ArrowDown", bubbles: true }),
	);
};

export const EventTagFilter: React.FC<EventTagFilterProps> = ({
	options,
	selected,
	onChange,
}) => {
	const [query, setQuery] = useState("");

	const suggestions = useMemo(() => {
		const needle = normaliseTag(query).toLocaleLowerCase("en-AU");
		return options
			.filter((opt) => !selected.includes(opt.id))
			.filter(
				(opt) =>
					!needle || opt.label.toLocaleLowerCase("en-AU").includes(needle),
			)
			.slice(0, MAX_SUGGESTIONS)
			.map((opt) => ({ value: opt.id, label: opt.label }));
	}, [options, query, selected]);

	return (
		<div
			className="em-tag-filter"
			onFocus={openIfClosed}
			onClick={openIfClosed}
		>
			<CCCombobox
				label="Tags"
				fullWidth
				size="sm"
				options={suggestions}
				/* Always null: the box adds to the selection, and the chips below
				   are the selection. */
				value={null}
				onChange={(value) => {
					setQuery("");
					if (value && !selected.includes(value))
						onChange([...selected, value]);
				}}
				onQueryChange={setQuery}
				placeholder={selected.length ? "Add another tag" : "All tags"}
				emptyLabel={options.length === 0 ? "No tags yet" : "No matching tags"}
			/>
			{selected.length > 0 && (
				<CCTagList
					tags={selected.map((id) => ({ id, label: id }))}
					onRemove={(id) => onChange(selected.filter((t) => t !== id))}
				/>
			)}
		</div>
	);
};
